/**
 * useContainerStatus Hook
 *
 * Manages container status fetching, caching, and lifecycle actions.
 *
 * @module features/tenants/hooks/useContainerStatus
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { Tenant, ContainerStatus, ContainerTransition } from '../types';
import {
  fetchContainerStatus as fetchContainerStatusApi,
  containerAction as containerActionApi,
  ContainerAction,
} from '../api/containerApi';

/**
 * Auth fetch function type.
 */
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Result of a container action.
 */
export interface ContainerActionResult {
  success: boolean;
  action: ContainerAction;
  finalStatus?: string;
  error?: string;
}

/**
 * Return type for useContainerStatus hook.
 */
export interface UseContainerStatusResult {
  /** Container statuses by tenant ID */
  statuses: Record<string, ContainerStatus>;
  /** Containers currently transitioning (start/stop/restart) */
  transitioning: Record<string, ContainerTransition>;
  /** Tenant ID of container action in progress */
  actionInProgress: string | null;

  /** Fetch status for a single tenant */
  fetchStatus: (tenantId: string) => Promise<ContainerStatus | null>;
  /** Refresh status for all container tenants */
  refreshAll: (tenants: Tenant[]) => void;
  /** Perform a container action with polling */
  performAction: (tenantId: string, action: ContainerAction) => Promise<ContainerActionResult>;
  /** Clear status for a tenant (e.g., after destroy) */
  clearStatus: (tenantId: string) => void;
}

// Polling configuration
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 36; // 36 * 5s = 180 seconds (3 minutes)
const INITIAL_DELAY_MS = 2000;

/**
 * Hook for managing container status and lifecycle actions.
 *
 * @param authFetch - Authenticated fetch function from useAuth
 * @returns Container statuses, transitions, and action functions
 *
 * @example
 * ```tsx
 * const {
 *   statuses,
 *   transitioning,
 *   fetchStatus,
 *   refreshAll,
 *   performAction,
 * } = useContainerStatus(authFetch);
 *
 * // Perform action
 * const result = await performAction(tenantId, 'restart');
 * if (result.success) {
 *   showSuccess('Containers restarted');
 * } else {
 *   showError(result.error);
 * }
 * ```
 */
export const useContainerStatus = (authFetch: AuthFetch): UseContainerStatusResult => {
  // Container status cache
  const [statuses, setStatuses] = useState<Record<string, ContainerStatus>>({});

  // Ref to access current statuses in async callbacks (avoids stale closure)
  const statusesRef = useRef(statuses);
  useEffect(() => { statusesRef.current = statuses; }, [statuses]);

  // Containers in transition (start/stop/restart in progress)
  const [transitioning, setTransitioning] = useState<Record<string, ContainerTransition>>({});

  // Current action in progress
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  /**
   * Fetch and cache status for a single tenant.
   */
  const fetchStatus = useCallback(async (tenantId: string): Promise<ContainerStatus | null> => {
    const statusData = await fetchContainerStatusApi(authFetch, tenantId);
    if (statusData) {
      setStatuses(prev => ({
        ...prev,
        [tenantId]: statusData
      }));
    }
    return statusData;
  }, [authFetch]);

  /**
   * Refresh status for all container-isolation tenants.
   */
  const refreshAll = useCallback((tenants: Tenant[]) => {
    tenants
      .filter(t => t.isolation_level === 'container')
      .forEach(tenant => fetchStatus(tenant.id));
  }, [fetchStatus]);

  /**
   * Clear status for a tenant (e.g., after destroying containers).
   */
  const clearStatus = useCallback((tenantId: string) => {
    setStatuses(prev => {
      const newStatuses = { ...prev };
      delete newStatuses[tenantId];
      return newStatuses;
    });
    setTransitioning(prev => {
      const newTransitioning = { ...prev };
      delete newTransitioning[tenantId];
      return newTransitioning;
    });
  }, []);

  /**
   * Perform a container action with polling until completion.
   */
  const performAction = useCallback(async (
    tenantId: string,
    action: ContainerAction
  ): Promise<ContainerActionResult> => {
    setActionInProgress(tenantId);

    try {
      // Initiate the action
      await containerActionApi(authFetch, tenantId, action);

      // Mark container as transitioning
      setTransitioning(prev => ({
        ...prev,
        [tenantId]: { action, startedAt: Date.now() }
      }));

      // Determine expected final state
      const expectedState = action === 'stop' ? 'stopped' : 'running';

      // Poll until we reach expected state or timeout
      const pollUntilComplete = async (attempt: number = 0): Promise<ContainerActionResult> => {
        // Fetch and get the status
        const statusData = await fetchStatus(tenantId);
        const currentStatus = statusData?.status;

        if (currentStatus === expectedState) {
          // Success! Clear transitioning state
          setTransitioning(prev => {
            const newState = { ...prev };
            delete newState[tenantId];
            return newState;
          });
          return { success: true, action, finalStatus: currentStatus };
        }

        if (attempt < MAX_POLL_ATTEMPTS) {
          // Continue polling after delay
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
          return pollUntilComplete(attempt + 1);
        }

        // Timeout - clear transitioning
        setTransitioning(prev => {
          const newState = { ...prev };
          delete newState[tenantId];
          return newState;
        });

        return {
          success: false,
          action,
          finalStatus: currentStatus,
          error: `Container operation is taking longer than expected. Current status: ${currentStatus || 'unknown'}. Please refresh to check.`
        };
      };

      // Start polling after brief delay for action to initiate
      await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY_MS));
      return await pollUntilComplete(0);

    } catch (err) {
      // Clear transitioning on error
      setTransitioning(prev => {
        const newState = { ...prev };
        delete newState[tenantId];
        return newState;
      });

      return {
        success: false,
        action,
        error: err instanceof Error ? err.message : `Failed to ${action} containers`
      };
    } finally {
      setActionInProgress(null);
    }
  }, [authFetch, fetchStatus]);

  return {
    statuses,
    transitioning,
    actionInProgress,
    fetchStatus,
    refreshAll,
    performAction,
    clearStatus,
  };
};
